import { acceptHMRUpdate, defineStore } from "pinia";
import { computed } from "vue";

import LanguageLevelService from "@/common/api/language-level.service";
import { LANGUAGE_GROUPS_PRIORITIZED } from "@/common/language-groups";
import { PACKAGE_GROUP_GYLDENDAL, PACKAGE_GROUP_ORDBOGEN } from "@/common/package-groups-types";
import type { LanguageLevel, Level } from "@/types/dictionary";
import type { LanguageGroup, PackageGroupId } from "@/types/site";

import { useAuthStore, useSearchStore, useSettingsStore } from ".";

const sortGroupsByPriority = (groups: LanguageLevel[]) =>
  [...groups].sort(
    (a, b) =>
      LANGUAGE_GROUPS_PRIORITIZED.indexOf(a.language) -
      LANGUAGE_GROUPS_PRIORITIZED.indexOf(b.language)
  );

export const useLanguageLevelStore = defineStore("language-level", () => {
  const activeLanguageLevels = computed(() => {
    const settingsStore = useSettingsStore();
    const languageGroupLevels = settingsStore.preferredPackageGroup?.languageLevels ?? [];

    return sortGroupsByPriority(languageGroupLevels);
  });

  const gyldendalLanguageLevels = computed(() => {
    const settingsStore = useSettingsStore();
    const packageGroup =
      settingsStore.packageGroups.find((group) => group.id === PACKAGE_GROUP_GYLDENDAL) ?? false;

    if (packageGroup === false) {
      return [];
    }

    const languageLevels = packageGroup.languageLevels.filter(
      (level) => !["arabic", "greenlandic", "russian"].includes(level.language)
    );

    return sortGroupsByPriority(languageLevels);
  });

  const ordbogenLanguageLevel = computed(() => {
    const authStore = useAuthStore();
    const settingsStore = useSettingsStore();

    const packageGroup =
      settingsStore.packageGroups.find((group) => group.id === PACKAGE_GROUP_ORDBOGEN) ?? false;

    if (packageGroup === false) {
      return authStore.user.level;
    }

    return packageGroup.languageLevels[0].level ?? authStore.user.level;
  });

  const activeLanguageLevel = computed(() => {
    const authStore = useAuthStore();
    const settingsStore = useSettingsStore();

    const languageGroup = activeLanguageLevels.value.find(
      (languageLevel) => languageLevel.language === settingsStore.activeLanguageGroup
    );

    return languageGroup?.level ?? authStore.user.level;
  });

  const updateLanguageLevel = async ({
    languageGroup,
    level,
    packageGroup
  }: {
    languageGroup: LanguageGroup;
    level: Level;
    packageGroup: PackageGroupId;
  }) => {
    const settingsStore = useSettingsStore();
    const searchStore = useSearchStore();

    await LanguageLevelService.updateLanguageLevel(packageGroup, level, languageGroup);

    settingsStore.setPackageGroupLanguageLevel(packageGroup, languageGroup, level);
    searchStore.resetSearchParameters();
  };

  const updateAllLanguageLevels = async ({
    level,
    packageGroup
  }: {
    level: Level;
    packageGroup: PackageGroupId;
  }) => {
    const settingsStore = useSettingsStore();
    const searchStore = useSearchStore();

    await LanguageLevelService.updateLanguageLevel(packageGroup, level);

    settingsStore.setPackageGroupLevels(packageGroup, level);
    searchStore.resetSearchParameters();
  };

  const resetAllLanguageLevels = async (packageGroup: PackageGroupId) => {
    const settingsStore = useSettingsStore();
    const searchStore = useSearchStore();

    const { data } = await LanguageLevelService.resetAllLevels(packageGroup);

    settingsStore.setPackageGroupLevels(packageGroup, data.data.level);
    searchStore.resetSearchParameters();
  };

  return {
    activeLanguageLevels,
    activeLanguageLevel,
    ordbogenLanguageLevel,
    gyldendalLanguageLevels,

    updateLanguageLevel,
    updateAllLanguageLevels,
    resetAllLanguageLevels
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useLanguageLevelStore, import.meta.hot));
}
